import React, { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import StyledInput from "../../components/StyledComponents/StyledInput";
import StyledText from "../../components/StyledComponents/StyledText";
import StyledButton from "../../components/StyledComponents/StyledButton";
import AuthSlider from "../../components/AuthSlider";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import ResetPassword from "../ResetPassword";
import {
  useGetEmployeeListingMutation,
  useGetUserMutation,
  useLoginMutation,
} from "../../services/apis";
import { useDispatch } from "react-redux";
import {
  emailValidationRules,
  passwordValidationRules,
  validateForm,
} from "../../utils/helper";
import { toast } from "react-toastify";
import { theme } from "../../theme";
import { clearTokens, setTokens, setUser } from "../../services/authSlice";

const Login = ({ isForgot }) => {
  const navigate = useNavigate();
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [login, { isLoading }] = useLoginMutation();
  const [getUser, { isLoading: isLoadingUser }] = useGetUserMutation();
  const [getEmployeeListing, { isLoading: isLoadingEmployee }] =
    useGetEmployeeListingMutation();
  // const [loginSSO, { isLoading: getSSOUrlLoading }] = useGetSSOLoginUrlMutation();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    isRemember: false,
  });
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});

  const [searchParams] = useSearchParams();

  const validationRules = {
    email: emailValidationRules,
    password: passwordValidationRules,
  };

  useEffect(() => {
    if (searchParams.has("token") && isForgot) {
      setShowResetPassword(true);
    } else if (isForgot) {
      navigate("/login");
    } else {
      setShowResetPassword(false);
    }
  }, [searchParams, isForgot, navigate]);
  const handleInputChange = (fieldName, value) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: "",
    }));
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateForm(formData, validationRules);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    try {
      const { data, error } = await login({
        ...formData,
        password: formData.password.trim(),
      });

      if (error) {
        toast.error(
          error?.errorMessage ||
            error?.data?.errorMessage ||
            "Login failed. Please try again."
        );
        console.error("Login error:", error);
        return;
      }
      dispatch(setTokens(data));
      let { data: user, error: userError } = await getUser();

      if (user?.corporateId) {
        const { data: employee } = await getEmployeeListing({
          email: user?.email,
        });
        if (employee?.content?.length)
          user = { ...employee?.content?.[0], userId: user.id };
      }

      if (userError || !user) {
        console.error("User error:", userError);
        toast.error(
          error?.errorMessage ||
          userError?.errorMessage ||
            userError?.data?.errorMessage ||
            "Error while fetching user details. Please try again."
        );
        dispatch(clearTokens());
        return;
      }

      if (user && typeof user === "object") {
        const { firstName = "", lastName = "" } = user;
        const name = [firstName, lastName].filter(Boolean).join(" ");

        dispatch(
          setUser({
            ...user,
            email: formData.email,
            name,
          })
        );
      }
      navigate("/");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("An unexpected error occurred. Please try again.");
    }
  };

  // const callHandleSSO = (provider) => handleSSO(provider, loginSSO, getUser, getEmployeeListing, dispatch, navigate);

  return (
    <AuthSlider>
      <h2 className="welcome-heading mb-2 text-center text-md-start">
        Welcome Back! Sign In
      </h2>
      <h5 className="signin-text text-center text-md-start">
        Please enter details to sign in to your account.
      </h5>
      <Form noValidate onSubmit={handleSubmit} className="py-2 py-md-4 pb-md-0">
        <StyledInput
          autoFocus
          label="Email Address"
          placeholder="Enter your email address here"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          required
          error={errors.email}
        />
        <StyledInput
          type="password"
          id="password"
          name="password"
          placeholder="Enter your password here"
          label="Password"
          autoComplete="on"
          style={{ paddingRight: "35px" }}
          value={formData.password}
          onChange={(e) => handleInputChange("password", e.target.value)}
          error={errors.password}
          color={theme.logoColor}
          required
        />
        <div className="d-flex align-items-center justify-content-end">
          <StyledText
            as={Link}
            to={"/reset-password"}
            className="fw-semibold "
            $fontSize="0.8rem"
            $fontSizeMd="12px"
          >
            Forget password?
          </StyledText>
        </div>
        <StyledButton
          _variant="primaryGradiant"
          type="submit"
          className="w-100 sign-in-btn p-md-3 mt-5"
          $fontWeightMd="600"
          loading={isLoading || isLoadingUser || isLoadingEmployee}
        >
          Sign In To Your Account
        </StyledButton>
        <StyledText as="div" className="text-center mt-2" $fontSize="0.9rem">
          Don't have an account yet?
          <StyledText
            as={Link}
            color="secondary"
            to="/signup"
            className="fw-semibold ms-1"
          >
            Create Here
          </StyledText>
        </StyledText>
      </Form>
      <ResetPassword
        show={showResetPassword}
        onHide={() => {
          setShowResetPassword(false);
          navigate("/login");
        }}
      />
      {/* <hr className="mt-4 mb-4" />
      <StyledButton
        onClick={() => callHandleSSO("MICROSOFT")}
        _variant='icon'
        disabled={getSSOUrlLoading}
        className='p-0 my-3 w-100'
      >
        <div className={`d-flex btn btn-outline-primary align-items-center gap-2 justify-content-center`}>
          <OutlookIcon />
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            Log in with Microsoft
          </div>
        </div>
      </StyledButton>
      <StyledButton
        onClick={() => callHandleSSO("GOOGLE")}
        _variant='icon'
        disabled={getSSOUrlLoading}
        className='p-0 mb-3 w-100'
      >
        <div className={`d-flex btn btn-outline-primary align-items-center gap-2 justify-content-center`}>
          <GoogleIcon />
          <div style={{ fontSize: '14px', fontWeight: '500' }}>
            Log in with Google
          </div>
        </div>
      </StyledButton> */}
    </AuthSlider>
  );
};

export default Login;
